import React, { useMemo } from "react";
import { Link } from "react-router";
import { AscDesc, StreamChat } from "stream-chat";
import { ChannelList, ChannelListItemUIProps, Chat, WithComponents } from "stream-chat-react";
import styled, { css } from "styled-components";
import { Environment } from "@core/config/environment";
import { Path } from "@core/routing/path";
import { MessaggingIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { Menu } from "@ui/components/menus/menu";
import { ChannelListPreview } from "@ui/components/messagingComponents/channel/channelListPreview";
import { EmptyChannel } from "@ui/components/messagingComponents/emptyChannel";
import { TOP_BAR_OFFSET, TOP_BAR_OFFSET_MOBILE } from "@ui/styles/utils";

interface ChatMessageProps extends React.HTMLAttributes<HTMLDivElement> {
	profileId: string;
	chatClient: StreamChat;
	unreadMessages: number;
	openChatMessage?: (profileId: string) => void;
}

const RecentChannels: React.FC<{ profileId: string; close: () => void }> = ({ profileId, close }) => {
	const filters = { type: "messaging", members: { $in: [profileId] } };
	const sort: { last_message_at: AscDesc } = { last_message_at: -1 };

	const overrides = useMemo(
		() => ({
			ChannelListItemUI: (props: ChannelListItemUIProps) => (
				<ChannelListPreview {...props} isChatMessage={false} profileId={profileId} close={close} />
			),
		}),
		[profileId, close]
	);

	return (
		<WithComponents overrides={overrides}>
			<ChannelList EmptyStateIndicator={EmptyChannel} filters={filters} sort={sort} />
		</WithComponents>
	);
};

const MessagingMenu: React.FC<ChatMessageProps> = ({ chatClient, unreadMessages, profileId }) => {
	return (
		<StyledMenu
			fixed="right"
			menuContentStyle={{ padding: 0, whiteSpace: "initial", height: "100%" }}
			renderMenuContent={(close) => (
				<Container>
					<Row>
						<Title>Messages récents</Title>
						<LinkText onClick={close} to={Path.Chatmessage(profileId)}>
							(voir ma messagerie)
						</LinkText>
					</Row>
					<Content>
						<Chat client={chatClient}>
							<RecentChannels profileId={profileId} close={close} />
						</Chat>
					</Content>
				</Container>
			)}
		>
			<Wrapper>
				<MessaggingIcon />
				{unreadMessages > 0 && (
					<RedCircle length={`${unreadMessages}`.length}>{unreadMessages > 999 ? 999 : unreadMessages}</RedCircle>
				)}
			</Wrapper>
		</StyledMenu>
	);
};

const StyledMenu = styled(Menu)`
	svg {
		flex-shrink: 0;
	}
`;

const Wrapper = styled.div`
	position: relative;
	width: 44px;
	height: 44px;
	border-radius: 22px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	display: flex;
	align-items: center;
	justify-content: center;
	svg path {
		fill: ${({ theme }) => theme.colors.secondary};
	}
`;

const Container = styled.div`
	width: 404px;
	max-width: 100vw;
	position: relative;
	height: 80vh;
	overflow: inherit;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	overflow-y: auto;
	overflow-x: hidden;
`;

const LinkText = styled(Link)`
	font-size: 15px;
	font-weight: 700;
	color: ${({ theme }) => (Environment.APP_VARIANT === "jaangal" ? theme.colors.primary : "rgba(32, 106, 248, 1)")};
	position: sticky;
	bottom: 0;
`;

const Row = styled.div`
	${row(0, "space-between", "center")}
	width: 100%;
	padding: 20px;
	top: -100px;
`;

const Content = styled.div`
	overflow: auto;
	max-height: calc(100vh - ${TOP_BAR_OFFSET}px);
	min-height: calc(100vh - ${TOP_BAR_OFFSET}px);
	@media (max-width: 900px) {
		max-height: calc(100vh - ${TOP_BAR_OFFSET_MOBILE}px);
	}
	> div {
		width: 404px;
		max-width: 100vw;
		overflow: hidden;
		padding-bottom: 20px;
	}
`;

const Title = styled.div`
	color: #13131a;
	font-family: Sora;
	font-size: 20px;
	font-style: normal;
	font-weight: 600;
`;

const RedCircle = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "length",
})<{ length: number }>`
	${({ length }) =>
		length < 2
			? css`
					width: 14px;
					height: 14px;
				`
			: length < 3
				? css`
						width: 16px;
						height: 16px;
					`
				: css`
						width: 22px;
						height: 22px;
					`}
	position: absolute;
	right: 0;
	top: 0;
	border-radius: 11px;
	background-color: ${({ theme }) => theme.colors.red};
	color: ${({ theme }) => theme.colors.white};
	font-size: 0.6875rem;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export { MessagingMenu };
